const handleInfinity = (value) =>
  value === Infinity || value === -Infinity ? 0 : value;

/**
 * Ajusta las tarifas de un item según su moneda para persistirlas: si la
 * moneda de la fila es CLP, convierte tarifa_diaria/tarifa_mensual de UF a
 * CLP y omite el descuento (se envía en 0). El estado interno mantiene las
 * tarifas en UF; esta conversión aplica solo en la capa de persistencia.
 * Las demás monedas quedan intactas. Sin redondeo para preservar la ida y
 * vuelta.
 */
const conTarifasEnMonedaDeFila = (item, monedaOptions = []) => {
  if (!item || item.moneda !== "CLP") return item;
  const valorUf =
    monedaOptions?.find((m) => m.codigo === "UF")?.tipoCambio || 1;
  const convertir = (valor) =>
    valor == null || valor === "Tarifa Variable" || isNaN(parseFloat(valor))
      ? valor
      : parseFloat(valor) * valorUf;
  return {
    ...item,
    tarifa_diaria: convertir(item.tarifa_diaria),
    tarifa_mensual: convertir(item.tarifa_mensual),
    descuento: 0,
  };
};

/**
 * Convierte un item de detalle (formato front, snake_case) al formato que
 * espera el endpoint sincronizarDetallesCotizacion.
 */
export const toSyncItem = (item, monedaOptions = []) => {
  const src = conTarifasEnMonedaDeFila(item, monedaOptions);
  return {
  id: src.id ?? null,
  idCotizacion: src.id_cotizacion ?? null,
  idEmpresa: src.id_empresa ?? null,
  relacion: src.relacion ?? null,
  descripcion: src.descripcion ?? null,
  idElemento: src.id_elemento ?? null,
  idProducto: src.id_elemento_producto ?? null,
  tramo: src.tramo && src.tramo !== "null" ? src.tramo : null,
  desde: src.desde ?? null,
  hasta: src.hasta ?? null,
  documentoCobro: src.cobro ?? src.documentoCobro ?? null,
  tipo: src.tipo ?? null,
  contrato: src.contrato ?? true,
  consolidado: src.consolidado ?? false,
  agrupamiento: src.agrupamiento ?? 0,
  unidadMedida: src.unidad ?? src.unidadMedida ?? "",
  cantidad: src.cantidad ?? 0,
  moneda: src.moneda ?? null,
  tarifaBase: src.tarifa_base ?? 0,
  tarifaCliente: src.tarifa_cliente ?? 0,
  tarifaDiaria: src.tarifa_diaria ?? 0,
  tarifaMensual: src.tarifa_mensual ?? 0,
  descuento: handleInfinity(src.descuento) ?? 0,
  activo: true,
  codigoProducto: src.codigo_producto ?? "",
  };
};

/**
 * Enriquece los subproductos con el id_elemento_producto de su producto ancla,
 * resolviendo el vínculo por descripción ("subproducto - producto").
 */
export const enriquecerSubproductosConProducto = (detalles) => {
  const productosMap = {};
  detalles.forEach((d) => {
    if (d.relacion === "producto") {
      productosMap[d.descripcion] = d;
    }
  });
  return detalles.map((d) => {
    if (d.relacion === "subproducto") {
      const parts = String(d.descripcion).split(" - ");
      const prodDesc = parts.length > 1 ? parts[1] : null;
      const prod = prodDesc ? productosMap[prodDesc] : null;
      if (prod) {
        return { ...d, id_elemento_producto: prod.id_elemento };
      }
    }
    return d;
  });
};

/**
 * Parámetros para insertDetalleCotizacion / updateDetalleCotizacion.
 * Modelo nuevo: para relacion='subproducto', idProducto es el producto ancla de la vigencia.
 */
export const buildDetalleParams = (item, monedaOptions = []) => {
  const {
    id,
    id_cotizacion,
    id_empresa,
    relacion,
    tramo,
    descripcion,
    desde,
    hasta,
    cobro,
    tipo,
    contrato,
    consolidado,
    agrupamiento,
    unidad,
    cantidad,
    moneda,
    tarifa_base,
    tarifa_cliente,
    tarifa_diaria,
    tarifa_mensual,
    descuento,
    id_elemento,
    id_elemento_producto,
  } = conTarifasEnMonedaDeFila(item, monedaOptions);

  const params = {
    idCotizacion: id_cotizacion,
    relacion: relacion,
    idElemento: id_elemento,
    idEmpresa: id_empresa,
    tramo: tramo || null,
    descripcion: descripcion,
    desde,
    hasta,
    documentoCobro: cobro,
    tipo: tipo,
    contrato,
    consolidado: consolidado,
    agrupamiento: agrupamiento,
    unidadMedida: unidad || "",
    cantidad: cantidad || 0,
    moneda,
    tarifaBase: tarifa_base || 0,
    tarifaCliente: tarifa_cliente || 0,
    tarifaDiaria: tarifa_diaria || 0,
    tarifaMensual: tarifa_mensual || 0,
    descuento: handleInfinity(descuento),
    activo: 1,
    id: id || null,
    codigoProducto: [],
  };

  // Omitir idProducto cuando no aplica (servicios/productos): el backend lo
  // declara como Integer opcional y el string "null" rompe la conversión (400).
  if (id_elemento_producto != null) {
    params.idProducto = id_elemento_producto;
  }

  return params;
};

/**
 * Parámetros para actualizarDetalleCotizacion.
 */
export const buildActualizarDetalleParams = (item, monedaOptions = []) => {
  const {
    id,
    id_cotizacion,
    id_empresa,
    relacion,
    tramo,
    descripcion,
    desde,
    hasta,
    cobro,
    tipo,
    contrato,
    consolidado,
    agrupamiento,
    unidad,
    cantidad,
    moneda,
    tarifa_base,
    tarifa_cliente,
    tarifa_diaria,
    tarifa_mensual,
    descuento,
    id_elemento,
    codigo_producto,
    id_elemento_producto,
  } = conTarifasEnMonedaDeFila(item, monedaOptions);

  return {
    idCotizacion: id_cotizacion,
    relacion: relacion,
    idElemento: id_elemento,
    idElementoProducto: id_elemento_producto || null,
    idEmpresa: id_empresa,
    tramo: tramo || null,
    descripcion: descripcion,
    desde,
    hasta,
    documentoCobro: cobro,
    tipo: tipo,
    contrato,
    consolidado: consolidado,
    agrupamiento: agrupamiento,
    unidadMedida: unidad || "",
    cantidad: cantidad || 0,
    moneda,
    tarifaBase: tarifa_base || 0,
    tarifaCliente: tarifa_cliente || 0,
    tarifaDiaria: tarifa_diaria || 0,
    tarifaMensual: tarifa_mensual || 0,
    descuento: handleInfinity(descuento) || 0,
    activo: 1,
    codigoProducto: codigo_producto,
    id: id || null,
  };
};
